/**
 * TipTap extension for #entity references (tickets, reports, vehicles).
 *
 * Trigger: # character
 * Renders as: <span data-type="refMention" data-id="refId" data-label="Label" data-ref-type="ticket">Label</span>
 * Serializes to markdown as: [Label](ticket:refId)
 */
import Mention from "@tiptap/extension-mention";
import { mergeAttributes } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { VueNodeViewRenderer } from "@tiptap/vue-2";
import { createMentionSerializeSpec } from "./mention-markdown";
import { createSuggestionRenderer, SuggestionItem } from "./suggestion-renderer";
import { MentionType } from "./mention-type.enum";
import MentionSuggestionList from "./MentionSuggestionList.vue";
import RefMentionNodeView from "./RefMentionNodeView.vue";

export interface ReferenceMentionOptions {
  /** Function to fetch reference suggestions for a query string */
  fetchSuggestions: (query: string) => Promise<SuggestionItem[]> | SuggestionItem[];
  HTMLAttributes: Record<string, any>;
}

export const ReferenceMention = Mention.extend<ReferenceMentionOptions>({
  name: MentionType.REF,

  addOptions() {
    return {
      fetchSuggestions: () => [],
      HTMLAttributes: {
        class: "mention mention--ref"
      },
      suggestion: {},
      suggestions: [],
      renderText({ node }: any) {
        return `#${node.attrs.label ?? node.attrs.id}`;
      },
      renderHTML({ node, options }: any) {
        return [
          "span",
          mergeAttributes({ "data-type": MentionType.REF }, options.HTMLAttributes),
          `#${node.attrs.label ?? node.attrs.id}`
        ];
      },
      deleteTriggerWithBackspace: false
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-id"),
        renderHTML: (attributes: any) => {
          if (!attributes.id) return {};
          return { "data-id": attributes.id };
        }
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-label"),
        renderHTML: (attributes: any) => {
          if (!attributes.label) return {};
          return { "data-label": attributes.label };
        }
      },
      refType: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-ref-type"),
        renderHTML: (attributes: any) => {
          if (!attributes.refType) return {};
          return { "data-ref-type": attributes.refType };
        }
      }
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${MentionType.REF}"]` }];
  },

  renderHTML({ node, HTMLAttributes }: any) {
    return [
      "span",
      mergeAttributes({ "data-type": MentionType.REF }, this.options.HTMLAttributes, HTMLAttributes),
      `#${node.attrs.label ?? node.attrs.id}`
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(RefMentionNodeView);
  },

  addStorage() {
    return {
      markdown: {
        serialize: createMentionSerializeSpec("#", (node: any) => node.attrs.refType ?? "ticket").serialize,
        // Parse hook is already registered by UserMention — it handles all mention prefixes.
        // We only define it here as a no-op to avoid duplicate DOM manipulation.
        parse: {}
      }
    };
  },

  addProseMirrorPlugins() {
    const fetchSuggestions = this.options.fetchSuggestions;

    const suggestion = {
      char: "#",
      items: ({ query }: { query: string }) => fetchSuggestions(query),
      render: createSuggestionRenderer(MentionSuggestionList),
      command: ({ editor, range, props }: any) => {
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const overrideSpace = nodeAfter?.text?.startsWith(" ");
        if (overrideSpace) {
          range.to += 1;
        }
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: this.name,
              attrs: {
                id: props.id,
                label: props.label,
                refType: props.refType
              }
            },
            { type: "text", text: " " }
          ])
          .run();
        editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
      },
      allow: ({ state, range }: any) => {
        const $from = state.doc.resolve(range.from);
        const type = state.schema.nodes[this.name];
        return !!$from.parent.type.contentMatch.matchType(type);
      }
    };

    return [
      Suggestion({
        ...suggestion,
        editor: this.editor,
        pluginKey: new PluginKey(MentionType.REF)
      })
    ];
  }
});
